import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, ChevronRight, Goal, ShieldAlert, Plus, Search, Pin } from "lucide-react";

export default function Tournaments() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pinned, setPinned] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState("");

  const load = () => {
    setLoading(true);
    api
      .get("/tournaments")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get("/me/pins").then((r) => setPinned(r.data.pinned || [])).catch(() => {});
  }, []);

  const togglePin = async (e, name) => {
    e.stopPropagation();
    const isPinned = pinned.includes(name);
    try {
      const { data } = isPinned
        ? await api.delete(`/me/pins/${encodeURIComponent(name)}`)
        : await api.post(`/me/pins/${encodeURIComponent(name)}`);
      setPinned(data.pinned || []);
      toast.success(isPinned ? `Unpinned ${name}` : `Pinned ${name} to Home`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      api
        .get("/tournaments/search", { params: { q } })
        .then((r) => setResults(r.data))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const addTournament = async (name) => {
    setAdding(name);
    try {
      await api.post("/categories", { name });
      toast.success(`${name} added`);
      setAddOpen(false);
      setQuery("");
      setResults([]);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAdding("");
    }
  };

  return (
    <div className="space-y-6" data-testid="tournaments-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            Competitions
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Live fixtures, game-market predictions and player prop cheatsheets for your competitions.
          </p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full font-semibold" data-testid="open-add-tournament">
              <Plus className="h-4 w-4" /> New Competition
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl max-w-md" data-testid="add-tournament-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Add a competition</DialogTitle>
              <DialogDescription>
                Search for any competition, no bets required to start tracking it.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="e.g. La Liga, Serie A, Champions League"
                  data-testid="tournament-search-input"
                  className="rounded-xl h-11 pl-9"
                  autoFocus
                />
              </div>
              <div className="max-h-72 overflow-y-auto space-y-1.5" data-testid="tournament-search-results">
                {searching ? (
                  <div className="h-20 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                ) : query.trim().length >= 2 && results.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    No competitions found for "{query.trim()}".
                  </p>
                ) : (
                  results.map((lg) => (
                    <button
                      key={lg.id}
                      onClick={() => addTournament(lg.name)}
                      disabled={!!adding}
                      data-testid="search-result-item"
                      className="w-full flex items-center gap-3 rounded-2xl p-2.5 hover:bg-secondary/70 transition-colors text-left disabled:opacity-50"
                    >
                      <div className="h-9 w-9 rounded-xl bg-secondary flex items-center justify-center shrink-0 overflow-hidden">
                        {lg.logo ? (
                          <img src={lg.logo} alt="" className="h-6 w-6 object-contain" />
                        ) : (
                          <ShieldAlert className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{lg.name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                          {lg.flag && <img src={lg.flag} alt="" className="h-3 w-4 object-cover rounded-sm" />}
                          {lg.country || "World"}
                          {lg.season ? ` · ${lg.season}` : ""}
                        </div>
                      </div>
                      {adding === lg.name ? (
                        <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
                      ) : (
                        <Plus className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className="glass rounded-3xl p-12 text-center" data-testid="tournaments-empty">
          <Goal className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-display text-xl font-bold">No competitions yet</h3>
          <p className="text-muted-foreground text-sm mt-1">
            Tap "New Competition" to add any competition, or tag a bet with one.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tournaments-list">
          {items.map((t, i) => {
            const lg = t.league;
            const matched = !!lg;
            return (
              <motion.button
                key={t.name}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                disabled={!matched}
                onClick={() => matched && navigate(`/tournaments/${encodeURIComponent(t.name)}`)}
                data-testid="tournament-card"
                className={
                  "glass rounded-3xl p-5 text-left flex items-center justify-between gap-3 transition-transform " +
                  (matched ? "hover:-translate-y-0.5 cursor-pointer" : "opacity-60 cursor-not-allowed")
                }
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-12 w-12 rounded-2xl bg-secondary/60 flex items-center justify-center shrink-0 overflow-hidden">
                    {lg?.logo ? (
                      <img src={lg.logo} alt="" className="h-8 w-8 object-contain" />
                    ) : (
                      <ShieldAlert className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-lg truncate">{t.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                      {lg?.flag && <img src={lg.flag} alt="" className="h-3 w-4 object-cover rounded-sm" />}
                      {matched ? `${lg.country || "World"} · ${lg.season}` : "No live data available"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => togglePin(e, t.name)}
                    data-testid="pin-competition"
                    title={pinned.includes(t.name) ? "Unpin from Home" : "Pin to Home"}
                    className={
                      "h-8 w-8 rounded-full flex items-center justify-center transition-colors " +
                      (pinned.includes(t.name)
                        ? "bg-primary/15 text-primary"
                        : "text-muted-foreground hover:bg-secondary")
                    }
                  >
                    <Pin className={"h-4 w-4 " + (pinned.includes(t.name) ? "fill-current" : "")} />
                  </span>
                  {matched && <ChevronRight className="h-5 w-5 text-muted-foreground" />}
                </div>
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
}
